import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { useApproveReviewMutation } from '../../../api/useApproveReviewMutation';
import { useRejectReviewMutation } from '../../../api/useRejectReviewMutation';
import { setAccountApexStatus } from '../../../../../ContextualSidebar/NetWorthSidebar/NetWorthSidebarActions';
import { APEX_ACCOUNT_STATUS_VIEW } from '../../../StatusViews.meta';
import { StatusViewsContext } from '../../../StatusViews.context';

interface Props {
  type: 'approve' | 'reject';
  onClose(): void;
  setStatusView: (view: string) => void;
}

export const ConfirmationModal = ({ type, onClose, setStatusView }: Props) => {
  const dispatch = useDispatch();

  const { clientId, accountId } = useContext(StatusViewsContext);

  const {
    mutateAsync: approveReview,
    isLoading: isApprovingReview,
    error: approveMutationError,
    reset: resetApproveReview,
  } = useApproveReviewMutation();

  const {
    mutateAsync: rejectReview,
    isLoading: isRejectingReview,
    error: rejectMutationError,
    reset: resetRejectReview,
  } = useRejectReviewMutation();

  const handlePrimaryAction = async () => {
    try {
      if (type === 'approve') {
        await approveReview({ clientId, accountId });
      } else {
        await rejectReview({ clientId, accountId });
      }

      onClose();
      dispatch(setAccountApexStatus(accountId, 'Pending'));
      setStatusView(APEX_ACCOUNT_STATUS_VIEW.PENDING_VIEW);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      size="medium"
      aria={{ labelledby: 'apex-advisor-review--confirmation-modal' }}
    >
      <ModalHeader as="h2" id="apex-advisor-review--confirmation-modal">
        {type === 'approve' ? 'Approve' : 'Reject'} Application?
      </ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        {approveMutationError && (
          <Alert
            variant="error"
            message={approveMutationError.message}
            onRequestClose={resetApproveReview}
            mb="16"
          />
        )}

        {rejectMutationError && (
          <Alert
            variant="error"
            message={rejectMutationError.message}
            onRequestClose={resetRejectReview}
            mb="16"
          />
        )}

        <Text color="neutral.500">
          Are you sure you want to {type === 'approve' ? 'approve' : 'reject'} this application?
        </Text>
      </ModalBody>

      <ModalFooter>
        <Button onClick={onClose} variant="subtleInactive" autoFocus mr="12">
          Cancel
        </Button>

        <Button
          isLoading={isApprovingReview || isRejectingReview}
          variant={type === 'approve' ? 'primary' : 'error'}
          onClick={handlePrimaryAction}
        >
          {type === 'approve' ? 'Approve' : 'Reject'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
